import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Modal,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { useAtom } from 'jotai';
import { selectedCustomerAtom } from './customerAtoms';
import { illustrationInputAtom } from './illustrationAtoms';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { customerRepository, illustrationRepository } from '../repositories';
import { generateUUID } from '../core/uuid';
import { calculateInsuranceAge, calculateChronologicalAge } from '../core/calculations';
import type { CustomerFull } from '../data/customers';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M06SelectCustomerScreen'>;

export const M06SelectCustomerScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProp<RootStackParamList, 'M06SelectCustomerScreen'>>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 1024;

  const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
  const [illustrationInput, setIllustrationInput] = useAtom(illustrationInputAtom);

  const selectedProductId = route.params?.productId || 'XUM';

  // Policy Owner States (Bên mua bảo hiểm)
  const [customerName, setCustomerName] = useState(
    selectedCustomer?.name || route.params?.customerName || illustrationInput.customerName || ''
  );
  const [customerDob, setCustomerDob] = useState(
    selectedCustomer?.dob || route.params?.customerDob || illustrationInput.customerDob || '24/01/1992'
  );
  const [customerAge, setCustomerAge] = useState<number>(
    selectedCustomer?.age || route.params?.customerAge || illustrationInput.customerAge || 34
  );
  const [customerGender, setCustomerGender] = useState<'Nam' | 'Nữ'>(
    selectedCustomer?.gender || route.params?.customerGender || illustrationInput.customerGender || 'Nam'
  );
  const [customerPhone, setCustomerPhone] = useState(
    selectedCustomer?.phone || route.params?.customerPhone || illustrationInput.customerPhone || '0901234567'
  );
  const [occupation, setOccupation] = useState(
    selectedCustomer?.occupation || 'Nhân viên văn phòng'
  );
  const [dependents, setDependents] = useState<number>(selectedCustomer?.dependentsCount || 0);

  // Insured Person Selection States
  const [selectedInsured, setSelectedInsured] = useState<string>(customerName || 'Bản thân');
  const [extraInsureds, setExtraInsureds] = useState<string[]>([]);
  const [showInsuredOptions, setShowInsuredOptions] = useState<boolean>(false);

  // Modal Control States
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAddInsuredModal, setShowAddInsuredModal] = useState(false);
  const [modalTab, setModalTab] = useState<'list' | 'custom'>('list');
  const [searchQuery, setSearchQuery] = useState('');

  // Custom Insured Modal Form States
  const [newInsuredName, setNewInsuredName] = useState('');
  const [newInsuredRelation, setNewInsuredRelation] = useState('Con NĐBH');

  // Temp Form States in Modal
  const [tempName, setTempName] = useState(customerName);
  const [tempDob, setTempDob] = useState(customerDob);
  const [tempAge, setTempAge] = useState(customerAge);
  const [tempGender, setTempGender] = useState(customerGender);
  const [tempPhone, setTempPhone] = useState(customerPhone);
  const [tempOccupation, setTempOccupation] = useState(occupation);

  // Sync state when selectedCustomer Atom changes
  useEffect(() => {
    if (selectedCustomer) {
      setCustomerName(selectedCustomer.name);
      setCustomerDob(selectedCustomer.dob);
      setCustomerAge(selectedCustomer.age);
      setCustomerGender(selectedCustomer.gender);
      setCustomerPhone(selectedCustomer.phone);
      setDependents(selectedCustomer.dependentsCount || 0);
      if (selectedCustomer.occupation) setOccupation(selectedCustomer.occupation);
      setSelectedInsured(selectedCustomer.name);
    }
  }, [selectedCustomer]);

  const openCustomerModal = () => {
    setTempName(customerName);
    setTempDob(customerDob);
    setTempAge(customerAge);
    setTempGender(customerGender);
    setTempPhone(customerPhone);
    setTempOccupation(occupation);
    setShowCustomerModal(true);
  };

  const handlePickCustomerFromList = (cust: CustomerFull) => {
    const computedAge = cust.dob ? calculateChronologicalAge(cust.dob) : cust.age;
    setCustomerName(cust.name);
    setCustomerDob(cust.dob);
    setCustomerAge(computedAge);
    setCustomerGender(cust.gender);
    setCustomerPhone(cust.phone);
    setOccupation(cust.occupation || 'Nhân viên văn phòng');
    setDependents(cust.dependentsCount || 0);
    setSelectedInsured(cust.name);

    const custState = {
      id: cust.id,
      name: cust.name,
      category: cust.category as any,
      dependentsCount: cust.dependentsCount,
      dob: cust.dob,
      age: computedAge,
      gender: cust.gender,
      phone: cust.phone,
      email: cust.email,
      labels: cust.labels as any,
      occupation: cust.occupation,
      createdAt: new Date().toISOString(),
    };
    setSelectedCustomer(custState);
    setShowCustomerModal(false);
  };

  const handleConfirmCustomCustomer = () => {
    if (!tempName.trim()) {
      Alert.alert('Chưa nhập họ tên', 'Vui lòng nhập đầy đủ Họ và tên Bên mua bảo hiểm.');
      return;
    }
    const computedAge = calculateChronologicalAge(tempDob);
    setCustomerName(tempName.trim());
    setCustomerDob(tempDob);
    setCustomerAge(computedAge);
    setCustomerGender(tempGender);
    setCustomerPhone(tempPhone);
    setOccupation(tempOccupation);
    setSelectedInsured(tempName.trim());
    setShowCustomerModal(false);
  };

  const handleAddExtraInsured = () => {
    if (!newInsuredName.trim()) {
      Alert.alert('Chưa nhập tên', 'Vui lòng nhập tên Người được bảo hiểm bổ sung.');
      return;
    }
    const fullName = `${newInsuredName.trim()} (${newInsuredRelation})`;
    if (!extraInsureds.includes(fullName)) {
      setExtraInsureds([...extraInsureds, fullName]);
      setSelectedInsured(fullName);
    }
    setNewInsuredName('');
    setShowAddInsuredModal(false);
  };

  const removeExtraInsured = (name: string) => {
    setExtraInsureds(extraInsureds.filter((i) => i !== name));
    if (selectedInsured === name) {
      setSelectedInsured(customerName || 'Bản thân');
    }
  };

  const handleNextStep = () => {
    if (!customerName.trim()) {
      Alert.alert(
        '⚠️ Chưa chọn Bên mua bảo hiểm',
        'Vui lòng chọn hoặc nhập thông tin Bên mua bảo hiểm trước khi chuyển sang bước tiếp theo.',
        [
          { text: 'Chọn ngay', onPress: openCustomerModal },
          { text: 'Hủy', style: 'cancel' },
        ]
      );
      return;
    }

    // Save Customer Info into Global Illustration Atom
    setIllustrationInput((prev) => ({
      ...prev,
      productId: selectedProductId,
      customerName: customerName.trim(),
      customerAge,
      customerGender,
      customerDob,
      customerPhone,
    }));

    const illustrationId = (route.params as any)?.illustrationId || generateUUID();
    const dateStr = new Date().toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    // Auto-save Step 1 draft to IndexedDB
    illustrationRepository.save({
      id: illustrationId,
      customerId: selectedCustomer?.id || 'KH-NHAP',
      proposalName: `Minh họa ${customerName.trim()}`,
      productCode: selectedProductId || 'XUM',
      customerName: customerName.trim(),
      customerAge,
      customerGender,
      sumAssured: '1.000.000.000',
      term: 20,
      freq: 'Năm',
      premium: '15.000.000',
      stage: 'new',
      lastUpdated: dateStr,
      expiryDate: 'Tự động gia hạn',
      selectedRiders: [],
      topUpAmount: 0,
    });

    // Navigate to Step 2 Screen (Product & Rider Configuration)
    navigation.navigate('M06ConfigureProductScreen', {
      illustrationId,
      productId: selectedProductId,
      customerName: customerName.trim(),
      customerAge,
      customerGender,
      customerDob,
    });
  };

  const [allCustomers, setAllCustomers] = useState<any[]>([]);

  useEffect(() => {
    customerRepository.getAll().then((list) => {
      setAllCustomers(list);
    });
  }, []);

  const filteredCustomers = allCustomers.filter((c: CustomerFull) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.phone.includes(searchQuery)
  );

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Header Title */}
        <View style={styles.stepHeader}>
          <Text style={styles.stepText}>Thông tin Khách hàng (BMBH & NĐBH)</Text>
        </View>

        {/* Survey Link Row (Tạm thời khóa) */}
        <TouchableOpacity
          style={[styles.surveyLinkRow, styles.disabledSurveyLink]}
          disabled={true}
          activeOpacity={1}
          accessibilityState={{ disabled: true }}
        >
          <Icon name="lock" size={15} color={colors.outlineVariant} />
          <Text style={styles.disabledSurveyLinkText}>
            Khảo sát nhu cầu tài chính ưu tiên <Text style={styles.lockBadgeText}>(Tạm khóa)</Text>
          </Text>
        </TouchableOpacity>

        {/* ROLE CONFIGURATION SCREEN (03_cac_buoc & Curex Original Step 1) */}
        <View style={[styles.roleContainer, isDesktop && styles.roleContainerDesktop]}>
          
          {/* Card 1: Bên Mua Bảo Hiểm */}
          <View style={[styles.roleCard, { borderTopColor: '#003366' }, isDesktop && styles.roleCardDesktop]}>
            <View style={styles.roleCardHeader}>
              <Text style={styles.roleCardTitle}>Bên Mua Bảo Hiểm</Text>
              <TouchableOpacity onPress={openCustomerModal} style={styles.arrowIconBtn}>
                <Icon name="arrow_forward" size={20} color="#EF4444" />
              </TouchableOpacity>
            </View>

            {!customerName.trim() ? (
              /* Prompt state when not selected */
              <TouchableOpacity style={styles.unselectedCard} onPress={openCustomerModal}>
                <View style={styles.warningIconCircle}>
                  <Icon name="warning" size={24} color="#D97706" />
                </View>
                <View style={styles.unselectedTextContainer}>
                  <Text style={styles.unselectedTitle}>⚠️ Cần chọn Bên mua bảo hiểm</Text>
                  <Text style={styles.unselectedSubtitle}>
                    Bấm vào đây để chọn từ danh sách khách hàng hoặc điền thông tin mới
                  </Text>
                </View>
                <View style={styles.selectBtnBadge}>
                  <Text style={styles.selectBtnBadgeText}>Chọn ngay</Text>
                  <Icon name="chevron_right" size={16} color={colors.white} />
                </View>
              </TouchableOpacity>
            ) : (
              /* Selected Customer View */
              <>
                <Text style={styles.ownerNameText}>{customerName}</Text>
                
                <View style={styles.ownerDetails}>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Giới tính</Text>
                    <Text style={styles.detailValue}>{customerGender}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Ngày sinh</Text>
                    <Text style={styles.detailValue}>{customerDob}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Tuổi</Text>
                    <Text style={styles.detailValue}>{customerAge} tuổi</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Nghề nghiệp</Text>
                    <Text style={styles.detailValue}>{occupation}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Người phụ thuộc</Text>
                    <View style={styles.dependentsSelector}>
                      <TouchableOpacity
                        style={styles.depBtn}
                        onPress={() => setDependents(Math.max(0, dependents - 1))}
                      >
                        <Text style={styles.depBtnText}>-</Text>
                      </TouchableOpacity>
                      <Text style={styles.depText}>{dependents}</Text>
                      <TouchableOpacity
                        style={styles.depBtn}
                        onPress={() => setDependents(dependents + 1)}
                      >
                        <Text style={styles.depBtnText}>+</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>

                <TouchableOpacity style={styles.changeOwnerBtn} onPress={openCustomerModal}>
                  <Icon name="edit" size={16} color={colors.primary} />
                  <Text style={styles.changeOwnerBtnText}>Thay đổi Bên mua bảo hiểm</Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* Card 2: Insured Card (Dark Blue Top Border - Matching Screenshot Left Box) */}
          {Boolean(customerName.trim()) && (
            <View style={[styles.roleCard, { borderTopColor: '#003366' }, isDesktop && styles.roleCardDesktop]}>
              <View style={styles.roleCardHeader}>
                <Text style={styles.roleCardTitle} numberOfLines={1}>
                  {selectedInsured === customerName || selectedInsured === 'Bản thân'
                    ? 'Bên Mua Bảo Hiểm đồng th...'
                    : `NĐBH Chính: ${selectedInsured}`}
                </Text>
                <TouchableOpacity
                  style={styles.arrowIconBtnCircle}
                  onPress={() => setShowInsuredOptions(!showInsuredOptions)}
                >
                  <Icon name="arrow_forward" size={16} color="#EF4444" />
                </TouchableOpacity>
              </View>

              <View style={styles.insuredCardBody}>
                <Text style={styles.ownerNameTextLarge}>
                  {selectedInsured === 'Bản thân' || selectedInsured === customerName
                    ? customerName || 'Bên mua HĐBH'
                    : selectedInsured}
                </Text>
                <View style={styles.ownerDetails}>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Giới tính</Text>
                    <Text style={styles.detailValue}>{customerGender}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Text style={styles.detailLabel}>Tuổi</Text>
                    <Text style={styles.detailValue}>{customerAge}</Text>
                  </View>
                </View>
              </View>

              <TouchableOpacity
                style={styles.changeInsuredFooterBtn}
                onPress={() => setShowInsuredOptions(!showInsuredOptions)}
                activeOpacity={0.7}
              >
                <View style={styles.redLeftCircleIcon}>
                  <Icon name="chevron_left" size={16} color="#EF4444" />
                </View>
                <Text style={styles.changeInsuredFooterBtnText}>Thay đổi Người Được Bảo Hiểm</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Card 3: Orange Panel - Lựa chọn Người Được Bảo Hiểm Khác (Matching Screenshot Right Box) */}
          {Boolean(customerName.trim()) && (isDesktop || showInsuredOptions) && (
            <View style={[styles.roleCard, { borderTopColor: '#F59E0B' }, isDesktop && styles.roleCardDesktop]}>
              <View style={styles.roleCardHeader}>
                <Text style={styles.orangeCardTitle}>Lựa chọn Người Được Bảo Hiểm Khác</Text>
              </View>

              <View style={styles.dividerRadioList}>
                {/* Option 1: BMBH đồng thời là NĐBH */}
                <TouchableOpacity
                  style={styles.dividerRadioRow}
                  onPress={() => {
                    setSelectedInsured(customerName || 'Bản thân');
                    setShowInsuredOptions(false);
                  }}
                >
                  <Icon
                    name={
                      selectedInsured === (customerName || 'Bản thân')
                        ? 'radio_button_checked'
                        : 'radio_button_unchecked'
                    }
                    size={22}
                    color={selectedInsured === (customerName || 'Bản thân') ? '#00A651' : '#94a3b8'}
                  />
                  <Text style={styles.radioLabelText}>BMBH đồng thời là NĐBH</Text>
                </TouchableOpacity>

                {/* Default relations from screenshot */}
                {['Vợ Chồng NĐBH', 'Con NĐBH', 'Ba Mẹ NĐBH', 'Anh Chị Em NĐBH'].map((relation) => (
                  <TouchableOpacity
                    key={relation}
                    style={styles.dividerRadioRow}
                    onPress={() => setSelectedInsured(relation)}
                  >
                    <Icon
                      name={selectedInsured === relation ? 'radio_button_checked' : 'radio_button_unchecked'}
                      size={22}
                      color={selectedInsured === relation ? '#00A651' : '#94a3b8'}
                    />
                    <Text style={styles.radioLabelText}>{relation}</Text>
                  </TouchableOpacity>
                ))}

                {/* Custom extra insureds */}
                {extraInsureds.map((name) => (
                  <View key={name} style={styles.dividerRadioRow}>
                    <TouchableOpacity
                      style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: spacing[3] }}
                      onPress={() => setSelectedInsured(name)}
                    >
                      <Icon
                        name={selectedInsured === name ? 'radio_button_checked' : 'radio_button_unchecked'}
                        size={22}
                        color={selectedInsured === name ? '#00A651' : '#94a3b8'}
                      />
                      <Text style={[styles.radioLabelText, { color: colors.primary }]}>{name}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => removeExtraInsured(name)}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      style={styles.removeInsuredBtn}
                    >
                      <Icon name="close" size={18} color="#EF4444" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>

              <TouchableOpacity
                style={styles.addInsuredFooterBtn}
                onPress={() => setShowAddInsuredModal(true)}
                activeOpacity={0.7}
              >
                <Icon name="add_circle" size={22} color="#EF4444" />
                <Text style={styles.addInsuredFooterBtnText}>Thêm Người Được Bảo Hiểm</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Bottom Action Bar (Only shown after Policy Owner is selected) */}
      {Boolean(customerName.trim()) && (
        <View style={styles.bottomBar}>
          <TouchableOpacity style={styles.nextBtn} onPress={handleNextStep}>
            <Text style={styles.nextBtnText}>TIẾP TỤC CẤU HÌNH SẢN PHẨM</Text>
            <Icon name="arrow_forward" size={18} color="#ffffff" />
          </TouchableOpacity>
        </View>
      )}

      {/* Customer Selection Modal Popup */}
      <Modal visible={showCustomerModal} transparent animationType="slide" onRequestClose={() => setShowCustomerModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Chọn hoặc Nhập Bên Mua Bảo Hiểm</Text>
              <TouchableOpacity onPress={() => setShowCustomerModal(false)} style={styles.closeBtn}>
                <Icon name="close" size={22} color={colors.onSurfaceVariant} />
              </TouchableOpacity>
            </View>

            {/* Mode Switcher Tabs */}
            <View style={styles.tabHeader}>
              <TouchableOpacity
                style={[styles.tabBtn, modalTab === 'list' && styles.tabBtnActive]}
                onPress={() => setModalTab('list')}
              >
                <Icon name="groups" size={16} color={modalTab === 'list' ? colors.primary : colors.onSurfaceVariant} />
                <Text style={[styles.tabBtnText, modalTab === 'list' && styles.tabBtnTextActive]}>
                  Từ Danh sách KH
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.tabBtn, modalTab === 'custom' && styles.tabBtnActive]}
                onPress={() => setModalTab('custom')}
              >
                <Icon name="edit" size={16} color={modalTab === 'custom' ? colors.primary : colors.onSurfaceVariant} />
                <Text style={[styles.tabBtnText, modalTab === 'custom' && styles.tabBtnTextActive]}>
                  Nhập thông tin mới
                </Text>
              </TouchableOpacity>
            </View>

            {/* Tab 1: Pick from list */}
            {modalTab === 'list' ? (
              <View style={{ flex: 1 }}>
                <View style={styles.modalSearchBox}>
                  <Icon name="search" size={18} color={colors.onSurfaceVariant} />
                  <TextInput
                    style={styles.modalSearchInput}
                    placeholder="Tìm theo tên hoặc số điện thoại..."
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                  />
                </View>
                <ScrollView style={{ flex: 1 }}>
                  {filteredCustomers.map((cust: CustomerFull) => (
                    <TouchableOpacity
                      key={cust.id}
                      style={styles.customerListItem}
                      onPress={() => handlePickCustomerFromList(cust)}
                    >
                      <View style={styles.listAvatarCircle}>
                        <Text style={styles.listAvatarText}>{cust.name.split(' ').pop()?.[0] || 'K'}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.listCustomerName}>{cust.name}</Text>
                        <Text style={styles.listCustomerSub}>
                          {cust.age} tuổi • {cust.gender} • SĐT: {cust.phone}
                        </Text>
                      </View>
                      <Icon name="chevron_right" size={20} color={colors.outlineVariant} />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            ) : (
              /* Tab 2: Custom Fill */
              <ScrollView style={{ flex: 1 }}>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Họ và tên *</Text>
                  <TextInput
                    style={styles.input}
                    value={tempName}
                    onChangeText={setTempName}
                    placeholder="Nguyễn Văn A..."
                  />
                </View>
                <View style={styles.rowTwoCols}>
                  <View style={[styles.formGroup, { flex: 1, marginRight: spacing[2] }]}>
                    <Text style={styles.label}>Ngày sinh (DD/MM/YYYY) *</Text>
                    <TextInput style={styles.input} value={tempDob} onChangeText={setTempDob} />
                  </View>
                  <View style={[styles.formGroup, { flex: 1, marginLeft: spacing[2] }]}>
                    <Text style={styles.label}>Tuổi *</Text>
                    <TextInput
                      style={styles.input}
                      value={tempAge.toString()}
                      keyboardType="numeric"
                      onChangeText={(v) => setTempAge(parseInt(v, 10) || 0)}
                    />
                  </View>
                </View>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Giới tính *</Text>
                  <View style={styles.genderRow}>
                    {(['Nam', 'Nữ'] as const).map((g) => (
                      <TouchableOpacity
                        key={g}
                        style={[styles.genderBtn, tempGender === g && styles.genderBtnActive]}
                        onPress={() => setTempGender(g)}
                      >
                        <Text style={[styles.genderBtnText, tempGender === g && styles.genderBtnTextActive]}>
                          {g}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Số điện thoại</Text>
                  <TextInput style={styles.input} value={tempPhone} onChangeText={setTempPhone} keyboardType="phone-pad" />
                </View>
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Nghề nghiệp</Text>
                  <TextInput style={styles.input} value={tempOccupation} onChangeText={setTempOccupation} />
                </View>

                <TouchableOpacity style={styles.modalSubmitBtn} onPress={handleConfirmCustomCustomer}>
                  <Text style={styles.modalSubmitBtnText}>Xác nhận chọn Bên mua BH</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Modal: Add Insured */}
      <Modal visible={showAddInsuredModal} transparent animationType="fade" onRequestClose={() => setShowAddInsuredModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Thêm Người Được Bảo Hiểm</Text>
              <TouchableOpacity onPress={() => setShowAddInsuredModal(false)}>
                <Icon name="close" size={22} color={colors.onSurfaceVariant} />
              </TouchableOpacity>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Họ và tên NĐBH *</Text>
              <TextInput
                style={styles.input}
                value={newInsuredName}
                onChangeText={setNewInsuredName}
                placeholder="Nhập tên..."
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Mối quan hệ với Bên mua BH</Text>
              <View style={styles.packageRow}>
                {['Vợ/Chồng', 'Con NĐBH', 'Ba Mẹ NĐBH'].map((rel) => (
                  <TouchableOpacity
                    key={rel}
                    style={[styles.packageChip, newInsuredRelation === rel && styles.packageChipActive]}
                    onPress={() => setNewInsuredRelation(rel)}
                  >
                    <Text style={[styles.packageChipText, newInsuredRelation === rel && styles.packageChipTextActive]}>
                      {rel}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TouchableOpacity style={styles.modalSubmitBtn} onPress={handleAddExtraInsured}>
              <Text style={styles.modalSubmitBtnText}>Thêm NĐBH</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 90,
  },
  stepHeader: {
    backgroundColor: '#ffffff',
    padding: spacing[3],
    borderRadius: borderRadius.md,
    marginBottom: spacing[3],
    borderWidth: 1,
    borderColor: colors.outline,
  },
  stepText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  indicatorRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  indicatorLine: {
    flex: 1,
    height: 4,
    backgroundColor: '#e1e2e9',
    borderRadius: 2,
  },
  indicatorActive: {
    backgroundColor: colors.secondary,
  },
  surveyLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[1],
    marginBottom: spacing[3],
    gap: spacing[2],
  },
  surveyLinkText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    textDecorationLine: 'underline',
  },
  disabledSurveyLink: {
    opacity: 0.6,
  },
  disabledSurveyLinkText: {
    fontSize: 13,
    color: colors.outlineVariant,
    textDecorationLine: 'none',
  },
  lockBadgeText: {
    fontSize: 11,
    fontStyle: 'italic',
    color: colors.outlineVariant,
  },
  /* ROLE CONFIGURATION STYLES (03_cac_buoc & Curex Original) */
  roleContainer: {
    flexDirection: 'column',
    gap: spacing[4],
  },
  roleContainerDesktop: {
    flexDirection: 'row',
  },
  roleCardDesktop: {
    maxWidth: 540,
    width: '49%',
  },
  roleCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderTopWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    flex: 1,
  },
  roleCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  roleCardTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurfaceVariant,
  },
  roleCardTitleGreen: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#00A651',
    marginBottom: spacing[3],
  },
  arrowIconBtn: {
    padding: spacing[1],
  },
  arrowIconBtnCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EF4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ownerNameText: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginBottom: spacing[3],
  },
  ownerNameTextLarge: {
    fontSize: 26,
    fontWeight: '300',
    color: '#0f172a',
    marginVertical: spacing[3],
  },
  insuredCardBody: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  changeInsuredFooterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: spacing[3],
    marginTop: spacing[4],
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: spacing[2],
  },
  redLeftCircleIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: '#EF4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  changeInsuredFooterBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#334155',
  },
  orangeCardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
    marginBottom: spacing[2],
  },
  dividerRadioList: {
    flex: 1,
  },
  dividerRadioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  radioLabelText: {
    fontSize: 14,
    color: '#334155',
    fontWeight: fontWeights.medium,
  },
  addInsuredFooterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: spacing[3],
    marginTop: spacing[3],
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: spacing[2],
  },
  addInsuredFooterBtnText: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
  },
  ownerDetails: {
    gap: spacing[2],
  },
  detailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  detailValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  dependentsSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  depBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.surfaceContainer,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
  },
  depBtnText: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  depText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    minWidth: 20,
    textAlign: 'center',
  },
  changeOwnerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.md,
    height: 38,
    gap: spacing[1],
    marginTop: spacing[3],
  },
  changeOwnerBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  radioList: {
    gap: spacing[2.5],
    marginBottom: spacing[4],
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    paddingVertical: spacing[1],
  },
  radioLabel: {
    fontSize: 14,
    color: colors.onSurface,
    fontWeight: '500',
  },
  removeInsuredBtn: {
    padding: spacing[1],
  },
  addInsuredBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#EF4444',
    borderStyle: 'dashed',
    borderRadius: borderRadius.md,
    height: 44,
    gap: spacing[2],
  },
  addInsuredText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#EF4444',
  },

  /* Unselected Customer Prompt Card */
  unselectedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF3C7',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    borderWidth: 1.5,
    borderColor: '#F59E0B',
    borderStyle: 'dashed',
  },
  warningIconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FDE68A',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing[2],
  },
  unselectedTextContainer: {
    flex: 1,
  },
  unselectedTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#92400E',
  },
  unselectedSubtitle: {
    fontSize: 11,
    color: '#B45309',
    marginTop: 2,
  },
  selectBtnBadge: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing[2],
    paddingVertical: 4,
    borderRadius: borderRadius.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  selectBtnBadgeText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: fontWeights.bold,
  },

  /* Bottom Bar */
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    padding: spacing[3],
    borderTopWidth: 1,
    borderTopColor: colors.outline,
  },
  nextBtn: {
    flex: 1,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
  },
  nextBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },

  /* Modal Styles */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
  },
  modalContent: {
    width: '100%',
    maxWidth: 520,
    maxHeight: '85%',
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing[4],
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[3],
    paddingBottom: spacing[2],
    borderBottomWidth: 1,
    borderBottomColor: colors.outline,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  closeBtn: {
    padding: 4,
  },
  tabHeader: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    padding: 4,
    marginBottom: spacing[3],
  },
  tabBtn: {
    flex: 1,
    height: 36,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    borderRadius: borderRadius.sm,
  },
  tabBtnActive: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.outline,
  },
  tabBtnText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  tabBtnTextActive: {
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  modalSearchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing[2],
    marginBottom: spacing[2],
    borderWidth: 1,
    borderColor: colors.outline,
  },
  modalSearchInput: {
    flex: 1,
    height: 40,
    marginLeft: spacing[1],
    fontSize: 13,
    color: colors.onSurface,
  },
  customerListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing[3],
    paddingHorizontal: spacing[2],
    borderBottomWidth: 1,
    borderBottomColor: colors.surfaceContainer,
  },
  listAvatarCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing[3],
  },
  listAvatarText: {
    color: colors.white,
    fontWeight: fontWeights.bold,
    fontSize: 15,
  },
  listCustomerName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  listCustomerSub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  formGroup: {
    marginBottom: spacing[3],
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.medium,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[1],
  },
  input: {
    height: 44,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outline,
    paddingHorizontal: spacing[2],
    fontSize: 14,
    color: colors.onSurface,
  },
  rowTwoCols: {
    flexDirection: 'row',
  },
  genderRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  genderBtn: {
    flex: 1,
    height: 40,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outline,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  genderBtnActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  genderBtnText: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
  },
  genderBtnTextActive: {
    color: colors.white,
    fontWeight: fontWeights.bold,
  },
  modalSubmitBtn: {
    backgroundColor: colors.primary,
    height: 46,
    borderRadius: borderRadius.lg,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: spacing[3],
    marginBottom: spacing[2],
  },
  modalSubmitBtnText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: fontWeights.bold,
  },
  packageRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[1],
  },
  packageChip: {
    backgroundColor: colors.white,
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outline,
  },
  packageChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  packageChipText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  packageChipTextActive: {
    color: colors.white,
    fontWeight: fontWeights.bold,
  },
});
